import React, { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

/**
 * OnboardingCelebration — confetti popup shown the first time a user finishes
 * every Onboarding Tasks item. Adapted from CSICelebration's confetti pattern,
 * but rendered as a dismissible overlay (not a full-page takeover) since the
 * dashboard should still be visible/usable behind it.
 *
 * Props: isOpen, onClose().
 */

const COLORS = ['#10b981', '#f59e0b', '#f43f5e', '#60a5fa', '#a78bfa'];

export default function OnboardingCelebration({ isOpen, onClose }) {
    const { isDarkMode } = useTheme();
    const reduced = typeof window !== 'undefined' && window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const pieces = useMemo(() => Array.from({ length: reduced ? 0 : 40 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        color: COLORS[i % COLORS.length],
        delay: Math.random() * 0.6,
        duration: 1.8 + Math.random() * 1.4,
        w: 6 + Math.random() * 6,
        rotate: Math.random() * 360,
    })), [reduced]);

    useEffect(() => {
        if (!isOpen) return;
        const t = setTimeout(onClose, reduced ? 1500 : 2800);
        return () => clearTimeout(t);
    }, [isOpen, onClose, reduced]);

    if (!isOpen) return null;

    const cardBg = isDarkMode ? 'bg-surface' : 'bg-surface';
    const heading = isDarkMode ? 'text-slate-100' : 'text-slate-900';
    const sub = isDarkMode ? 'text-slate-400' : 'text-slate-600';

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6">
            <div
                className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-300"
                onClick={onClose}
            />

            <div className={`relative w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300 ${cardBg}`}>
                <style>{`@keyframes onboarding-fall{0%{transform:translateY(-12vh) rotate(0);opacity:0}8%{opacity:1}100%{transform:translateY(112vh) rotate(720deg);opacity:1}}`}</style>

                {pieces.map(p => (
                    <span key={p.id} aria-hidden="true" style={{
                        position: 'absolute', top: 0, left: `${p.left}%`,
                        width: p.w, height: p.w * 0.6, background: p.color, borderRadius: 2,
                        transform: `rotate(${p.rotate}deg)`,
                        animation: `onboarding-fall ${p.duration}s linear ${p.delay}s forwards`,
                    }} />
                ))}

                <button
                    onClick={onClose}
                    className={`absolute top-3 right-3 z-10 p-2 rounded-full transition-colors ${
                        isDarkMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
                    }`}
                    aria-label="Close"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="relative text-center px-8 py-12">
                    <motion.div
                        initial={{ scale: 0, rotate: -20 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                        className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-primary-500 shadow-lg shadow-primary-500/30"
                    >
                        <Check size={44} className="text-white" strokeWidth={3} />
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                        className={`text-2xl font-extrabold ${heading}`}
                    >
                        Onboarding Complete! 🎉
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}
                        className={`mt-2 text-sm ${sub}`}
                    >
                        You've connected LinkedIn and added your first 3 tasks. Your dashboard is all set up.
                    </motion.p>
                </div>
            </div>
        </div>
    );
}
